/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from 'react';
import type { PopulateParam, StrapiListRequest } from '@/interfaces/strapi';
import { PAGINATION_SIZE } from '@/constants';

type UseStrapiParamsOptions = {
	fields?: string[];
	search?: string;
	searchField?: string;
	dateRange?: { from?: Date; to?: Date };
	dateField?: string;
	page?: number;
	pageSize?: number | string;
	sort?: string;
	populate?: PopulateParam;
	additionalFilters?: Record<string, any>;
	query?: Record<string, string>;
};

export const useStrapiParams = ({
	fields,
	search,
	searchField = 'name',
	dateRange,
	dateField = 'publishedAt',
	page,
	pageSize = PAGINATION_SIZE,
	sort,
	populate,
	additionalFilters,
	query,
}: UseStrapiParamsOptions): StrapiListRequest & {
	fields?: string[];
	sort?: string;
	populate?: PopulateParam;
	additionalFilters?: Record<string, any>;
	query?: Record<string, string>;
} =>
	useMemo(() => {
		const filters: Record<string, any> = {};

		// Filtro de texto
		if (search?.trim()) {
			filters[searchField] = { $containsi: search.trim() };
		}

		// Filtro por fecha
		if (dateRange?.from) {
			filters[dateField] = {
				...(filters[dateField] ?? {}),
				$gte: dateRange.from.toISOString().split('T')[0],
			};
		}
		if (dateRange?.to) {
			filters[dateField] = {
				...(filters[dateField] ?? {}),
				$lte: dateRange.to.toISOString().split('T')[0],
				...filters[dateField],
			};
		}

		const mergedFilters = {
			...filters,
			...(additionalFilters ?? {}),
		};

		const result: StrapiListRequest & {
			fields?: string[];
			sort?: string;
			populate?: PopulateParam;
			additionalFilters?: Record<string, any>;
			query?: Record<string, string>;
		} = {
			filter: Object.keys(mergedFilters).length > 0 ? mergedFilters : undefined,
		};

		if (page !== undefined && pageSize !== undefined) {
			result.pagination = { page, pageSize };
		}

		if (fields?.length) {
			result.fields = fields;
		}

		if (sort) {
			result.sort = sort;
		}

		if (populate) {
			result.populate = populate;
		}

		if (query) result.query = query;

		return result;
	}, [
		search,
		searchField,
		dateRange?.from?.toISOString(),
		dateRange?.to?.toISOString(),
		dateField,
		page,
		pageSize,
		sort,
		JSON.stringify(fields ?? []),
		JSON.stringify(populate ?? {}),
		additionalFilters ? JSON.stringify(additionalFilters) : '',
		JSON.stringify(query ?? {}),
	]);
